import moment from "moment";
import React from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import { chain, first, forEach, get, last, map, orderBy, reduce, round, uniq, values } from "lodash";

import Assignment from "@material-ui/icons/Assignment";
import ECCard from "components/EnergyCompany/Cards/ECCard.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import InfoGrid from "components/EnergyCompany/Grid/InfoGrid.jsx";
import InfoGridItem from "components/EnergyCompany/Grid/InfoGridItem.jsx";
import Table from "components/Table/Table.jsx";
import { localeString } from "services/utils";

const styles = {
  tableWrapper: {
    marginTop: "30px",
  },
};

const SazoFlex = ({ checkedAgentProfiles, classes, title }) => {
  const getSazonalidadeMinima = sazonalidadeMinima => {
    const value = round(sazonalidadeMinima * 100);
    return value < 70 ? "Sazonalizar mensalmente" : `${value}%`;
  };

  const getSazonalidadeMaxima = sazonalidadeMaxima => {
    const value = round(sazonalidadeMaxima * 100);
    return value > 130 ? "Sazonalizar mensalmente" : `${value}%`;
  };

  const calculateMWMedioPerMonth = () => {
    let mWMedioPerMonth = {};

    forEach(checkedAgentProfiles, agentProfile => {
      forEach(agentProfile.energiesValues, (data, month) => {
        mWMedioPerMonth[month] = mWMedioPerMonth[month] || { leve: 0, medio: 0, pesado: 0, total: 0 };
        mWMedioPerMonth[month].leve += get(data.consumoMWMedPorPatamar, "leve", 0);
        mWMedioPerMonth[month].medio += get(data.consumoMWMedPorPatamar, "medio", 0);
        mWMedioPerMonth[month].pesado += get(data.consumoMWMedPorPatamar, "pesado", 0);
        mWMedioPerMonth[month].total += get(data.consumoMWMedPorPatamar, "total", 0);
      });
    });

    return mWMedioPerMonth;
  };

  const calculateModulacoes = mWMedioPerMonth => {
    let modulacoes = {};

    forEach(mWMedioPerMonth, (data, month) => {
      modulacoes[month] = modulacoes[month] || { leve: 0, medio: 0, pesado: 0 };
      modulacoes[month].leve = (data.leve / data.total) || 0;
      modulacoes[month].medio = (data.medio / data.total) || 0;
      modulacoes[month].pesado = (data.pesado / data.total) || 0;
    });

    return modulacoes;
  };

  const calculateModulacaoMinMax = modulacoes => {
    const data = chain(modulacoes)
      .values()
      .map(monthData => values(monthData))
      .flatten()
      .orderBy()
      .value();

    return [first(data) * 100, last(data) * 100];
  };

  const calculateConsumoMinMax = mWMedioPerMonth => {
    const data = chain(mWMedioPerMonth)
      .values()
      .map(monthData => monthData.total)
      .orderBy()
      .value();

    return [first(data), last(data)];
  };

  const calculateRecursoTotal = () => {
    let recursoTotal = 0;

    forEach(checkedAgentProfiles, agentProfile => (
      forEach(agentProfile.energiesValues, data => recursoTotal += data.recurso)
    ));

    return recursoTotal;
  };

  const getTableData = mWMedioPerMonth => {
    return map(mWMedioPerMonth, (value, key) => [moment(key).format("MMM Y"), localeString(value.leve), localeString(value.medio), localeString(value.pesado), localeString(value.total)]);
  };

  const selectedSubmarkets = orderBy(uniq(map(checkedAgentProfiles, 'submarket')));
  let mWMedioPerMonth = calculateMWMedioPerMonth();
  const montante = reduce(values(mWMedioPerMonth), (sum, data) => sum + data.total, 0) / values(mWMedioPerMonth).length;
  let modulacoes = calculateModulacoes(mWMedioPerMonth);
  const [modulacaoMinima, modulacaoMaxima] = calculateModulacaoMinMax(modulacoes);
  const [consumoMinimo, consumoMaximo] = calculateConsumoMinMax(mWMedioPerMonth);
  const recursoTotal = calculateRecursoTotal();
  const sazonalidadeMaxima = consumoMaximo / consumoMinimo;
  const sazonalidadeMinima = 2 - sazonalidadeMaxima;

  const shouldShow = !!values(mWMedioPerMonth).length;

  return (
    <GridContainer>
      <GridItem md={12} sm={12} xs={12}>
        <ECCard
          content={consumoMaximo === 0 ? (
            recursoTotal === 0 ? (
              <h4>Agente sem ponto de carga associada.</h4>
            ) : (
              <h4>Agente sem ponto de carga associada. O recurso deste agente pode estar sendo utilizado por outro agente.</h4>
            )) : (
              <div>
                <InfoGrid>
                  <InfoGridItem label="Montante" value={shouldShow && `${localeString(montante)} MW Médio`} />
                  <InfoGridItem label="Flexibilidade Máxima" value={shouldShow && `${round(consumoMaximo/montante*100)}%`} />
                </InfoGrid>

                <InfoGrid>
                  <InfoGridItem label="Submercado" value={selectedSubmarkets.join(", ")} />
                  <InfoGridItem label="Flexibilidade Mínima" value={shouldShow && `${round(consumoMinimo/montante*100)}%`} />
                </InfoGrid>

                <InfoGrid>
                  <InfoGridItem label="Modulação Máxima" value={shouldShow && `${round(modulacaoMaxima)}%`} />
                  <InfoGridItem label="Sazonalidade Máxima" value={shouldShow && getSazonalidadeMaxima(sazonalidadeMaxima)} />
                </InfoGrid>

                <InfoGrid>
                  <InfoGridItem label="Modulação Mínima" value={shouldShow && `${round(modulacaoMinima)}%`} />
                  <InfoGridItem label="Sazonalidade Mínima" value={shouldShow && getSazonalidadeMinima(sazonalidadeMinima)} />
                </InfoGrid>

                <div className={classes.tableWrapper}>
                  <Table
                    tableData={getTableData(mWMedioPerMonth)}
                    tableHead={["Mês", "Leve", "Médio", "Pesado", "Total"]}
                    tableHeaderColor="primary"
                  />
                </div>
              </div>
            )
          }
          icon={<Assignment />}
          title={title}
        />
      </GridItem>
    </GridContainer>
  );
};

SazoFlex.defaultProps = {
  title: "Perfil de Contratação Sugerida",
};

SazoFlex.propTypes = {
  checkedAgentProfiles: PropTypes.arrayOf(PropTypes.shape({
    submarket: PropTypes.string.isRequired,
  })).isRequired,
  classes: PropTypes.object.isRequired,
  title: PropTypes.string,
};

export { SazoFlex as SazoFlexComponent };
export default withStyles(styles)(SazoFlex);



// WEBPACK FOOTER //
// ./src/views/Dashboard/partials/SazoFlex.jsx